<script setup lang="ts">
import { useCreatePublicPage } from "@/api/public-page-query";
import { useSnackbarStore } from "@/stores";
import { mdiFilePlus } from "@mdi/js";
import { useForm } from "@soft-db/form";
import { HTTPResponseError } from "@soft-db/http";
import { CreatePublicPage, UserScope } from "@soft-db/monitoring-api-client";
import { FormField, Icon, InputGroup, Select, SelectOption } from "@soft-db/ui";
import { useI18n } from "vue-i18n";
import * as z from "zod";
import AppDialog from "../common/AppDialog.vue";
import DistScope from "./components/DistScope.vue";
import SpaceScope from "./components/SpaceScope.vue";

const props = defineProps<{
	scope: UserScope;
}>();
const emit = defineEmits<{
	(e: "create", id: string): void;
}>();

const { t } = useI18n();

const snackbarStore = useSnackbarStore();

const dialog = defineModel<boolean>("dialog");

function close() {
	clear();
	dialog.value = false;
}

const publicPageCreation = useCreatePublicPage();
const { useField, handleSubmit, clear } = useForm<CreatePublicPage>();

const name = useField("name", { schema: z.string().trim().min(1) });

const preferredLang = useField("preferredLang", { isRequired: true });
const langs = computed(() => [
	{
		text: "Français (Canada)",
		value: "fr-CA",
	},
	{
		text: "English (Canada)",
		value: "en-CA",
	},
	{
		text: "English (United States)",
		value: "en-US",
	},
]);

const createPage = handleSubmit(async (page) => {
	try {
		const newPage = await publicPageCreation.mutateAsync({
			scope: props.scope,
			page,
		});

		snackbarStore.openOnMessage(t("snackbar.publicPageCreated"));
		emit("create", newPage.id);
		close();
	} catch (e) {
		if (e instanceof HTTPResponseError) {
			const { response } = e;
			if ("statusCode" in response && response.statusCode === 409) {
				snackbarStore.openOnError(t("users.alreadyExist"));
			}
		}
		throw e;
	}
});
</script>

<template>
	<AppDialog
		v-model="dialog"
		class="w-[500px]"
		:actionText="t('common.create')"
		@close="close"
		@click:action="createPage"
	>
		<template #title>
			<Icon :d="mdiFilePlus" />
			<span>{{ t("actions.createPublicPage") }}</span>
		</template>
		<template #content>
			<div class="mb-2">
				<SpaceScope v-if="scope.type === 'space'" :id="scope.id" />
				<DistScope v-if="scope.type === 'distributor'" :id="scope.id" />
			</div>

			<FormField class="w-full" :label="t('common.name')" :errors="name.errors.value">
				<InputGroup class="w-full">
					<input v-model="name.data.value" />
				</InputGroup>
			</FormField>

			<FormField class="w-full" :label="t('profile.language')" :errors="preferredLang.errors.value">
				<Select v-model="preferredLang.data.value" class="w-full">
					<SelectOption v-for="l of langs" :key="l.value" :value="l.value">
						{{ l.text }}
					</SelectOption>
				</Select>
			</FormField>
		</template>
	</AppDialog>
</template>
